/**
 * CreatorStudioPanel — Institution-Scoped Content Publishing
 *
 * Views:
 *   list     → browse all visible content, filter by type/status
 *   create   → new content form with question builder
 *   edit     → same form, pre-populated
 *   detail   → full preview with questions + workflow actions
 */

import { useState, useEffect, useCallback } from "react";
import { useAuthStore } from "@/store/authStore";

const API = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

async function apiFetch(path, opts = {}) {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const TYPES = [
  { value: "quiz",     label: "Quiz",     icon: "🧠" },
  { value: "material", label: "Material", icon: "📄" },
  { value: "lesson",   label: "Lektion",  icon: "📖" },
];

const STATUSES = {
  draft:     { label: "Entwurf",      color: "#6b7280", bg: "rgba(107,114,128,0.12)", border: "rgba(107,114,128,0.3)" },
  review:    { label: "In Prüfung",   color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  border: "rgba(245,158,11,0.3)" },
  published: { label: "Veröffentlicht", color: "#10b981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)" },
  rejected:  { label: "Abgelehnt",    color: "#ef4444", bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.3)" },
};

// ── Tiny UI primitives ────────────────────────────────────────────────────────
function Btn({ children, onClick, disabled, variant = "primary", size = "md", className = "" }) {
  const base = "inline-flex items-center justify-center gap-1.5 font-bold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:scale-100 disabled:cursor-not-allowed";
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-sm" };
  const variants = {
    primary: { background: "linear-gradient(135deg,#8b5cf6,#06b6d4)", color: "#fff", boxShadow: "0 0 16px rgba(139,92,246,0.3)" },
    danger:  { background: "rgba(239,68,68,0.12)",  border: "1px solid rgba(239,68,68,0.3)",  color: "#f87171" },
    ghost:   { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af" },
    success: { background: "rgba(16,185,129,0.12)",  border: "1px solid rgba(16,185,129,0.3)",  color: "#34d399" },
    warn:    { background: "rgba(245,158,11,0.12)",  border: "1px solid rgba(245,158,11,0.3)",  color: "#fbbf24" },
    purple:  { background: "rgba(139,92,246,0.12)",  border: "1px solid rgba(139,92,246,0.3)",  color: "#a78bfa" },
  };
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${sizes[size]} ${className}`} style={variants[variant]}>
      {children}
    </button>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", multiline }) {
  const shared = {
    value,
    onChange,
    placeholder,
    className: "w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-gray-600 outline-none transition-all",
    style: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)" },
    onFocus: (e) => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.background = "rgba(139,92,246,0.06)"; },
    onBlur:  (e) => { e.target.style.borderColor = "rgba(139,92,246,0.18)"; e.target.style.background = "rgba(255,255,255,0.04)"; },
  };
  return (
    <div>
      {label && <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5">{label}</label>}
      {multiline
        ? <textarea rows={3} {...shared} />
        : <input type={type} {...shared} />
      }
    </div>
  );
}

function StatusPill({ status }) {
  const s = STATUSES[status] || STATUSES.draft;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}>
      {s.label}
    </span>
  );
}

function TypeIcon({ type }) {
  return <span>{TYPES.find((t) => t.value === type)?.icon || "📄"}</span>;
}

function Toast({ msg, type = "success", onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-5 py-3 rounded-2xl border text-sm font-bold"
      style={{
        background: "rgba(8,8,24,0.97)", backdropFilter: "blur(20px)",
        borderColor: type === "success" ? "rgba(52,211,153,0.4)" : "rgba(239,68,68,0.4)",
        color:       type === "success" ? "#34d399"              : "#f87171",
        boxShadow:  `0 0 30px ${type === "success" ? "rgba(52,211,153,0.2)" : "rgba(239,68,68,0.2)"}`,
      }}>
      {type === "success" ? "✅" : "❌"} {msg}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <svg className="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5">
        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
      </svg>
    </div>
  );
}

// ── Question Builder ──────────────────────────────────────────────────────────
function QuestionBuilder({ questions, onChange }) {
  const addQuestion = () => {
    onChange([...questions, { text: "", type: "single", explain: "", answers: [
      { text: "", isCorrect: true }, { text: "", isCorrect: false },
    ]}]);
  };

  const removeQuestion = (qi) => {
    onChange(questions.filter((_, i) => i !== qi));
  };

  const updateQ = (qi, field, val) => {
    const next = questions.map((q, i) => i === qi ? { ...q, [field]: val } : q);
    onChange(next);
  };

  const addAnswer = (qi) => {
    const next = questions.map((q, i) => i === qi
      ? { ...q, answers: [...(q.answers || []), { text: "", isCorrect: false }] }
      : q
    );
    onChange(next);
  };

  const removeAnswer = (qi, ai) => {
    const next = questions.map((q, i) => i === qi
      ? { ...q, answers: q.answers.filter((_, j) => j !== ai) }
      : q
    );
    onChange(next);
  };

  const updateA = (qi, ai, field, val) => {
    const next = questions.map((q, i) => {
      if (i !== qi) return q;
      let answers = q.answers.map((a, j) => j === ai ? { ...a, [field]: val } : a);
      // for "single" type: only one correct answer
      if (field === "isCorrect" && val && q.type === "single") {
        answers = answers.map((a, j) => ({ ...a, isCorrect: j === ai }));
      }
      return { ...q, answers };
    });
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">
          Fragen ({questions.length})
        </p>
        <Btn onClick={addQuestion} size="sm" variant="purple">+ Frage</Btn>
      </div>

      {questions.map((q, qi) => (
        <div key={qi} className="rounded-2xl border p-4 space-y-3"
          style={{ background: "rgba(139,92,246,0.04)", borderColor: "rgba(139,92,246,0.18)" }}>
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <Input
                placeholder={`Frage ${qi + 1}…`}
                value={q.text}
                onChange={(e) => updateQ(qi, "text", e.target.value)}
              />
              <div className="flex gap-2">
                {["single", "multi", "text"].map((t) => (
                  <button key={t}
                    onClick={() => updateQ(qi, "type", t)}
                    className="px-2 py-1 rounded-lg text-[10px] font-bold transition-all"
                    style={{
                      background: q.type === t ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${q.type === t ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.08)"}`,
                      color: q.type === t ? "#a78bfa" : "#6b7280",
                    }}
                  >
                    {t === "single" ? "Einzelwahl" : t === "multi" ? "Mehrfach" : "Freitext"}
                  </button>
                ))}
              </div>
            </div>
            <Btn onClick={() => removeQuestion(qi)} variant="danger" size="sm">✕</Btn>
          </div>

          {q.type !== "text" && (
            <div className="space-y-2 pl-2">
              {(q.answers || []).map((a, ai) => (
                <div key={ai} className="flex items-center gap-2">
                  <button
                    onClick={() => updateA(qi, ai, "isCorrect", !a.isCorrect)}
                    className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center text-xs transition-all"
                    style={{
                      background: a.isCorrect ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.04)",
                      border: `1.5px solid ${a.isCorrect ? "#10b981" : "rgba(255,255,255,0.12)"}`,
                      color: "#10b981",
                    }}
                  >
                    {a.isCorrect ? "✓" : ""}
                  </button>
                  <input
                    className="flex-1 px-2 py-1.5 rounded-lg text-sm text-white placeholder-gray-600 outline-none"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                    placeholder={`Antwort ${ai + 1}…`}
                    value={a.text}
                    onChange={(e) => updateA(qi, ai, "text", e.target.value)}
                  />
                  <Btn onClick={() => removeAnswer(qi, ai)} variant="danger" size="sm">✕</Btn>
                </div>
              ))}
              <Btn onClick={() => addAnswer(qi)} size="sm" variant="ghost">+ Antwort</Btn>
            </div>
          )}

          <Input
            placeholder="Erklärung (optional)…"
            value={q.explain || ""}
            onChange={(e) => updateQ(qi, "explain", e.target.value)}
          />
        </div>
      ))}

      {questions.length === 0 && (
        <div className="text-center py-6 text-gray-600 text-sm">
          Noch keine Fragen — klicke "+ Frage" um zu beginnen
        </div>
      )}
    </div>
  );
}

// ── Content Form (create + edit) ──────────────────────────────────────────────
function ContentForm({ initial, institutions, onSave, onCancel, currentUser }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    type:          initial?.type          || "quiz",
    title:         initial?.title         || "",
    description:   initial?.description   || "",
    institutionId: initial?.institutionId || currentUser?.institutionId || "",
    questions:     initial?.questions     || [],
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const isPlatformAdmin = currentUser?.role === "admin" || currentUser?.role === "solid_ground";

  const save = async () => {
    if (!form.title.trim()) { setError("Titel ist Pflichtfeld"); return; }
    setSaving(true);
    setError("");
    try {
      const payload = {
        type:          form.type,
        title:         form.title,
        description:   form.description,
        institutionId: form.institutionId || null,
        questions:     form.questions,
      };
      const data = isEdit
        ? await apiFetch(`/studio/${initial.id}`, { method: "PUT",  body: JSON.stringify(payload) })
        : await apiFetch("/studio",               { method: "POST", body: JSON.stringify(payload) });
      onSave(data.content);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="text-gray-600 hover:text-white transition-colors text-lg">←</button>
        <div>
          <h2 className="text-lg font-black text-white">{isEdit ? "Inhalt bearbeiten" : "Neuen Inhalt erstellen"}</h2>
          <p className="text-xs text-gray-600">{isEdit ? `ID: ${initial.id.slice(0,8)}…` : "Neuer Entwurf"}</p>
        </div>
      </div>

      {/* Type selector */}
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-2">Typ</label>
        <div className="flex gap-2">
          {TYPES.map((t) => (
            <button key={t.value}
              onClick={() => setForm((f) => ({ ...f, type: t.value }))}
              className="flex-1 py-3 rounded-xl flex flex-col items-center gap-1 transition-all hover:scale-[1.02]"
              style={{
                background: form.type === t.value ? "rgba(139,92,246,0.15)" : "rgba(255,255,255,0.03)",
                border: `1.5px solid ${form.type === t.value ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.08)"}`,
              }}
            >
              <span className="text-2xl">{t.icon}</span>
              <span className="text-xs font-bold" style={{ color: form.type === t.value ? "#a78bfa" : "#6b7280" }}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <Input
        label="Titel"
        value={form.title}
        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        placeholder="Thema oder Titel des Inhalts…"
      />

      <Input
        label="Beschreibung (optional)"
        multiline
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        placeholder="Worum geht es in diesem Inhalt?…"
      />

      {isPlatformAdmin && institutions.length > 0 && (
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5">Institution</label>
          <select
            value={form.institutionId}
            onChange={(e) => setForm((f) => ({ ...f, institutionId: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none appearance-none"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)" }}
          >
            <option value="" style={{ background: "#0d0d1e" }}>Keine Institution (global)</option>
            {institutions.map((inst) => (
              <option key={inst.id} value={inst.id} style={{ background: "#0d0d1e" }}>{inst.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Question builder (for quiz type) */}
      {form.type === "quiz" && (
        <div className="rounded-2xl border p-4" style={{ borderColor: "rgba(139,92,246,0.12)", background: "rgba(139,92,246,0.02)" }}>
          <QuestionBuilder
            questions={form.questions}
            onChange={(qs) => setForm((f) => ({ ...f, questions: qs }))}
          />
        </div>
      )}

      {error && (
        <div className="px-4 py-3 rounded-xl text-sm font-bold text-red-400"
          style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <Btn onClick={save} disabled={saving} variant="primary" size="lg">
          {saving ? "Speichern…" : isEdit ? "Änderungen speichern" : "Entwurf erstellen"}
        </Btn>
        <Btn onClick={onCancel} variant="ghost" size="lg">Abbrechen</Btn>
      </div>
    </div>
  );
}

// ── Content Detail (preview + workflow) ───────────────────────────────────────
function ContentDetail({ id, currentUser, onBack, onEdit, onUpdated }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [toast, setToast] = useState(null);

  const isPlatformAdmin = currentUser?.role === "admin" || currentUser?.role === "solid_ground";
  const isAuthor = content?.authorId === currentUser?.id;

  const load = useCallback(async () => {
    try {
      const data = await apiFetch(`/studio/${id}`);
      setContent(data.content);
    } catch { setContent(null); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const action = async (endpoint, body = {}) => {
    setWorking(true);
    try {
      const data = await apiFetch(`/studio/${id}/${endpoint}`, { method: "POST", body: JSON.stringify(body) });
      setToast({ msg: "Aktion erfolgreich", type: "success" });
      await load();
      if (onUpdated) onUpdated(data.content);
    } catch (e) {
      setToast({ msg: e.message, type: "error" });
    } finally { setWorking(false); }
  };

  if (loading) return <Spinner />;
  if (!content) return <p className="text-gray-600 text-sm">Inhalt nicht gefunden.</p>;

  const s = STATUSES[content.status] || STATUSES.draft;
  const typeInfo = TYPES.find((t) => t.value === content.type) || TYPES[0];

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      {/* Back + Edit */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-gray-600 hover:text-white transition-colors text-lg">←</button>
        {(isAuthor || isPlatformAdmin) && content.status !== "published" && (
          <Btn onClick={() => onEdit(content)} variant="ghost" size="sm">✏️ Bearbeiten</Btn>
        )}
      </div>

      {/* Header card */}
      <div className="rounded-2xl border p-5" style={{ background: "rgba(139,92,246,0.05)", borderColor: "rgba(139,92,246,0.2)" }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{typeInfo.icon}</span>
            <div>
              <h2 className="text-xl font-black text-white">{content.title}</h2>
              <p className="text-xs text-gray-600">{typeInfo.label} · von {content.author?.name}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusPill status={content.status} />
            {content.isAwarded && (
              <span className="text-xs font-bold text-yellow-400" style={{ textShadow: "0 0 8px rgba(251,191,36,0.5)" }}>
                🏆 Ausgezeichnet
              </span>
            )}
          </div>
        </div>
        {content.description && (
          <p className="text-sm text-gray-400">{content.description}</p>
        )}
        {content.institution && (
          <p className="text-xs text-gray-600 mt-2">🏛️ {content.institution.name}</p>
        )}
        {content.rejectedNote && (
          <div className="mt-3 px-3 py-2 rounded-xl text-xs text-red-400"
            style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <strong>Ablehnung:</strong> {content.rejectedNote}
          </div>
        )}
      </div>

      {/* Questions */}
      {content.questions?.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">
            Fragen ({content.questions.length})
          </p>
          {content.questions.map((q, qi) => (
            <div key={q.id} className="rounded-2xl border p-4 space-y-3"
              style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.06)" }}>
              <p className="text-sm font-bold text-white">
                <span className="text-gray-600 mr-2">{qi + 1}.</span>{q.text}
              </p>
              {q.answers?.length > 0 && (
                <div className="space-y-1.5">
                  {q.answers.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-2 rounded-xl"
                      style={{
                        background: a.isCorrect ? "rgba(16,185,129,0.08)" : "rgba(255,255,255,0.03)",
                        border: `1px solid ${a.isCorrect ? "rgba(16,185,129,0.3)" : "rgba(255,255,255,0.06)"}`,
                      }}>
                      <span className="text-xs" style={{ color: a.isCorrect ? "#10b981" : "#6b7280" }}>
                        {a.isCorrect ? "✓" : "○"}
                      </span>
                      <span className="text-sm text-white">{a.text}</span>
                    </div>
                  ))}
                </div>
              )}
              {q.explain && (
                <p className="text-xs text-gray-600 italic">{q.explain}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Workflow actions */}
      <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Aktionen</p>
        <div className="flex flex-wrap gap-2">
          {/* Author: submit for review */}
          {(isAuthor || isPlatformAdmin) && (content.status === "draft" || content.status === "rejected") && (
            <Btn onClick={() => action("submit")} disabled={working} variant="warn">
              📤 Zur Prüfung einreichen
            </Btn>
          )}

          {/* Admin: publish */}
          {isPlatformAdmin && content.status === "review" && (
            <>
              <Btn onClick={() => action("publish", { award: false })} disabled={working} variant="success">
                ✅ Veröffentlichen
              </Btn>
              <Btn onClick={() => action("publish", { award: true })} disabled={working} variant="success">
                🏆 Veröffentlichen + Auszeichnen
              </Btn>
            </>
          )}

          {/* Admin: reject */}
          {isPlatformAdmin && content.status === "review" && !showReject && (
            <Btn onClick={() => setShowReject(true)} disabled={working} variant="danger">
              ❌ Ablehnen
            </Btn>
          )}

          {/* Admin: toggle award on published */}
          {isPlatformAdmin && content.status === "published" && (
            <Btn onClick={() => action("award")} disabled={working} variant="warn">
              {content.isAwarded ? "🔔 Auszeichnung entfernen" : "🏆 Auszeichnen"}
            </Btn>
          )}

          {/* Delete */}
          {(isAuthor || isPlatformAdmin) && content.status !== "published" && (
            <Btn onClick={async () => {
              if (!confirm("Inhalt wirklich löschen?")) return;
              setWorking(true);
              try {
                await apiFetch(`/studio/${id}`, { method: "DELETE" });
                onBack();
              } catch (e) { setToast({ msg: e.message, type: "error" }); }
              finally { setWorking(false); }
            }} disabled={working} variant="danger">
              🗑️ Löschen
            </Btn>
          )}
        </div>

        {/* Reject note input */}
        {showReject && (
          <div className="space-y-2">
            <Input
              placeholder="Begründung (optional)…"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <div className="flex gap-2">
              <Btn onClick={() => { action("reject", { note: rejectNote }); setShowReject(false); }} disabled={working} variant="danger" size="sm">
                Ablehnen bestätigen
              </Btn>
              <Btn onClick={() => setShowReject(false)} variant="ghost" size="sm">Abbrechen</Btn>
            </div>
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="text-xs text-gray-700 space-y-0.5">
        <p>Erstellt: {new Date(content.createdAt).toLocaleString("de-DE")}</p>
        {content.publishedAt && <p>Veröffentlicht: {new Date(content.publishedAt).toLocaleString("de-DE")}</p>}
        {content.isAwarded && content.awardedAt && <p>Ausgezeichnet: {new Date(content.awardedAt).toLocaleString("de-DE")} von {content.awardedBy?.name}</p>}
      </div>
    </div>
  );
}

// ── Content List ──────────────────────────────────────────────────────────────
function ContentList({ currentUser, institutions, onOpen, onNew, refresh }) {
  const [items, setItems]    = useState([]);
  const [total, setTotal]    = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState({ status: "", type: "", search: "" });
  const [page, setPage]       = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 20 });
      if (filter.status) qs.set("status", filter.status);
      if (filter.type)   qs.set("type",   filter.type);
      if (filter.search) qs.set("search", filter.search);
      const data = await apiFetch(`/studio?${qs}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refresh) load(); }, [refresh, load]);

  const reviewCount = items.filter((i) => i.status === "review").length;
  const isPlatformAdmin = currentUser?.role === "admin" || currentUser?.role === "solid_ground";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            🎬 Creator Studio
            {reviewCount > 0 && isPlatformAdmin && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                style={{ background: "rgba(245,158,11,0.2)", border: "1px solid rgba(245,158,11,0.4)", color: "#fbbf24" }}>
                {reviewCount} zur Prüfung
              </span>
            )}
          </h2>
          <p className="text-xs text-gray-600 mt-0.5">
            {total} {total === 1 ? "Inhalt" : "Inhalte"} gefunden
          </p>
        </div>
        <Btn onClick={onNew} variant="primary">+ Neuer Inhalt</Btn>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <input
          className="px-3 py-2 rounded-xl text-sm text-white placeholder-gray-600 outline-none"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)", minWidth: "180px" }}
          placeholder="🔍 Suchen…"
          value={filter.search}
          onChange={(e) => { setFilter((f) => ({ ...f, search: e.target.value })); setPage(1); }}
        />
        <select
          value={filter.type}
          onChange={(e) => { setFilter((f) => ({ ...f, type: e.target.value })); setPage(1); }}
          className="px-3 py-2 rounded-xl text-sm text-white outline-none appearance-none"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)" }}
        >
          <option value="" style={{ background: "#0d0d1e" }}>Alle Typen</option>
          {TYPES.map((t) => <option key={t.value} value={t.value} style={{ background: "#0d0d1e" }}>{t.icon} {t.label}</option>)}
        </select>
        <select
          value={filter.status}
          onChange={(e) => { setFilter((f) => ({ ...f, status: e.target.value })); setPage(1); }}
          className="px-3 py-2 rounded-xl text-sm text-white outline-none appearance-none"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)" }}
        >
          <option value="" style={{ background: "#0d0d1e" }}>Alle Status</option>
          {Object.entries(STATUSES).map(([k, v]) => (
            <option key={k} value={k} style={{ background: "#0d0d1e" }}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {loading ? <Spinner /> : items.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <p className="text-4xl mb-3">🎬</p>
          <p className="font-bold text-white">Noch keine Inhalte</p>
          <p className="text-sm mt-1">Erstelle deinen ersten Inhalt!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id}
              onClick={() => onOpen(item.id)}
              className="flex items-center gap-4 px-4 py-3 rounded-2xl border cursor-pointer transition-all hover:scale-[1.005]"
              style={{
                background: "rgba(255,255,255,0.02)",
                borderColor: item.status === "review" ? "rgba(245,158,11,0.25)" : "rgba(255,255,255,0.06)",
              }}
            >
              <span className="text-2xl w-10 flex-shrink-0 text-center">
                <TypeIcon type={item.type} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-white truncate">{item.title}</p>
                  {item.isAwarded && <span className="text-xs text-yellow-400">🏆</span>}
                </div>
                <p className="text-xs text-gray-600 truncate">
                  {item.author?.name} · {item._count?.questions ?? 0} Fragen
                  {item.institution && ` · ${item.institution.name}`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <StatusPill status={item.status} />
                <span className="text-gray-700">›</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Btn onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} variant="ghost" size="sm">← Zurück</Btn>
          <span className="text-xs text-gray-600">Seite {page} / {Math.ceil(total / 20)}</span>
          <Btn onClick={() => setPage((p) => p + 1)} disabled={page >= Math.ceil(total / 20) || loading} variant="ghost" size="sm">Weiter →</Btn>
        </div>
      )}
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
export default function CreatorStudioPanel({ currentUser, institutions = [] }) {
  const [view, setView]     = useState("list"); // "list" | "create" | "edit" | "detail"
  const [detailId, setDetailId] = useState(null);
  const [editContent, setEditContent] = useState(null);
  const [listRefresh, setListRefresh] = useState(0);
  const [toast, setToast]   = useState(null);

  const openDetail = (id) => { setDetailId(id); setView("detail"); };
  const openEdit   = (c)  => { setEditContent(c); setView("edit"); };
  const openCreate = ()   => { setEditContent(null); setView("create"); };
  const goList     = ()   => { setView("list"); setDetailId(null); setEditContent(null); };

  const handleSave = () => {
    setToast({ msg: "Inhalt gespeichert!", type: "success" });
    setListRefresh((n) => n + 1);
    goList();
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      {view === "list" && (
        <ContentList
          currentUser={currentUser}
          institutions={institutions}
          onOpen={openDetail}
          onNew={openCreate}
          refresh={listRefresh}
        />
      )}

      {(view === "create" || view === "edit") && (
        <ContentForm
          initial={editContent}
          institutions={institutions}
          currentUser={currentUser}
          onSave={handleSave}
          onCancel={goList}
        />
      )}

      {view === "detail" && detailId && (
        <ContentDetail
          id={detailId}
          currentUser={currentUser}
          onBack={goList}
          onEdit={openEdit}
          onUpdated={() => setListRefresh((n) => n + 1)}
        />
      )}
    </div>
  );
}
